import React, { useState, useEffect } from "react";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";

interface InterviewFeedbackModalProps {
  isVisible: boolean;
  onSubmit: (feedbackData: FeedbackData) => void;
  onCancel: () => void;
  screeningId: string;
  isViewMode?: boolean;
  existingFeedback?: FeedbackData;
}

export interface FeedbackData {
  attitude: number;
  communication: number;
  confidence: number;
  culturalFit: number;
  problemSolving: number;
  roleKnowledge: number;
  additionalComments: string;
}

const InterviewFeedbackModal: React.FC<InterviewFeedbackModalProps> = ({
  isVisible,
  onSubmit,
  onCancel,
  screeningId,
  isViewMode = false,
  existingFeedback,
}) => {
  const interviewApi = useInterviewApi();
  const toast = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ratings, setRatings] = useState<FeedbackData>(
    existingFeedback || {
      attitude: 0,
      communication: 0,
      confidence: 0,
      culturalFit: 0,
      problemSolving: 0,
      roleKnowledge: 0,
      additionalComments: "",
    },
  );

  const [hoveredRatings, setHoveredRatings] = useState<{ [key: string]: number }>({});

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleStarClick = (category: keyof FeedbackData, value: number) => {
    if (category !== "additionalComments" && !isViewMode) {
      setRatings({ ...ratings, [category]: value });
    }
  };

  const handleStarHover = (category: string, starIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
    const starElement = event.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const hoverX = event.clientX - rect.left;
    const starWidth = rect.width;

    // Divide each star into 5 segments: 0.2, 0.4, 0.6, 0.8, 1.0
    const segmentWidth = starWidth / 5;
    const segment = Math.ceil(hoverX / segmentWidth);

    // Calculate the hover rating: (starIndex - 1) + (segment * 0.2)
    const hoverRating = starIndex - 1 + segment * 0.2;

    setHoveredRatings({ ...hoveredRatings, [category]: hoverRating });
  };

  const handleStarMove = (category: string, starIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
    // Update hover value as mouse moves within the star
    handleStarHover(category, starIndex, event);
  };

  const handleStarLeave = (category: string) => {
    const newHoveredRatings = { ...hoveredRatings };
    delete newHoveredRatings[category];
    setHoveredRatings(newHoveredRatings);
  };

  const calculateOverallScore = (): string => {
    const numericRatings = [
      ratings.attitude,
      ratings.communication,
      ratings.confidence,
      ratings.culturalFit,
      ratings.problemSolving,
      ratings.roleKnowledge,
    ];
    const totalRatings = numericRatings.filter((r) => r > 0).length;
    if (totalRatings === 0) return "0.0";
    const sum = numericRatings.reduce((acc, curr) => acc + curr, 0);
    const average = sum / 6; // Total 6 categories
    return average.toFixed(1);
  };

  const handleSubmit = () => {
    // Validate that all ratings are provided
    const allRatingsProvided =
      ratings.attitude > 0 &&
      ratings.communication > 0 &&
      ratings.confidence > 0 &&
      ratings.culturalFit > 0 &&
      ratings.problemSolving > 0 &&
      ratings.roleKnowledge > 0;

    if (!allRatingsProvided) {
      toast.error("Please provide all ratings before submitting.");
      return;
    }

    if (!screeningId) {
      toast.error("Invalid screening ID. Unable to submit feedback.");
      return;
    }

    // Calculate overall score
    const overallScore = parseFloat(calculateOverallScore());

    // Prepare API payload
    const payload = {
      screening_id: screeningId,
      attitude: ratings.attitude,
      communication: ratings.communication,
      confidence: ratings.confidence,
      cultural_fit: ratings.culturalFit,
      problem_solving: ratings.problemSolving,
      role_knowledge: ratings.roleKnowledge,
      additional_comments: ratings.additionalComments || "",
      overall_score: overallScore,
    };

    setIsSubmitting(true);

    interviewApi.callSubmitInterviewFeedback(
      payload,
      (message: string, resp: any) => {
        setIsSubmitting(false);
        toast.success(message || "Feedback submitted successfully!");
        onSubmit(ratings);
      },
      (error: string) => {
        setIsSubmitting(false);
        toast.error(error || "Failed to submit feedback. Please try again.");
      },
    );
  };

  const handleStarSegmentClick = (
    category: keyof FeedbackData,
    starIndex: number,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (category === "additionalComments" || isViewMode) return;

    const starElement = event.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const starWidth = rect.width;

    // Divide each star into 5 segments: 0.2, 0.4, 0.6, 0.8, 1.0
    const segmentWidth = starWidth / 5;
    const segment = Math.ceil(clickX / segmentWidth);

    // Calculate the rating: (starIndex - 1) + (segment * 0.2)
    const rating = starIndex - 1 + segment * 0.2;

    setRatings({ ...ratings, [category]: rating });
  };

  const renderStars = (category: keyof FeedbackData, label: string) => {
    const currentRating = ratings[category] as number;
    const hoveredValue = hoveredRatings[category] || 0;
    const displayValue = isViewMode ? currentRating : hoveredValue || currentRating;

    return (
      <div className="feedback-rating-row">
        <div className="feedback-rating-label">{label}</div>
        <div className="feedback-stars-container">
          {[1, 2, 3, 4, 5].map((star) => {
            // Calculate fill percentage for partial stars
            const fillPercentage =
              displayValue >= star ? 100 : displayValue > star - 1 ? (displayValue - (star - 1)) * 100 : 0;

            return (
              <div
                key={star}
                className="feedback-star-wrapper"
                onClick={(e) => !isViewMode && handleStarSegmentClick(category, star, e)}
                onMouseEnter={(e) => !isViewMode && handleStarHover(category, star, e)}
                onMouseMove={(e) => !isViewMode && handleStarMove(category, star, e)}
                onMouseLeave={() => !isViewMode && handleStarLeave(category)}
                style={isViewMode ? { cursor: "default" } : {}}
              >
                {fillPercentage > 0 && fillPercentage < 100 ? (
                  // Partial star (gradient fill)
                  <div className="feedback-star-container">
                    <i className="fa fa-star feedback-star feedback-star-empty"></i>
                    <div className="feedback-star-fill" style={{ width: `${fillPercentage}%` }}>
                      <i className="fa fa-star feedback-star feedback-star-filled"></i>
                    </div>
                  </div>
                ) : (
                  // Full or empty star
                  <i className={`fa fa-star feedback-star ${star <= displayValue ? "feedback-star-filled" : ""}`}></i>
                )}
              </div>
            );
          })}
          <span className="feedback-rating-value">{`${currentRating.toFixed(1)} / 5`}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="feedback-modal-overlay" onClick={onCancel}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="feedback-modal-close" onClick={onCancel}>
          <i className="fa fa-times"></i>
        </button>

        <h2 className="feedback-modal-title">{isViewMode ? "Live Interview Report" : "Interview Feedback"}</h2>
        <p className="feedback-modal-subtitle">
          {isViewMode
            ? "View the interview feedback provided for this candidate."
            : "Please rate the candidate based on the interview session."}
        </p>

        <div className="feedback-ratings-section">
          {renderStars("attitude", "Attitude")}
          {renderStars("communication", "Communication")}
          {renderStars("confidence", "Confidence")}
          {renderStars("culturalFit", "Cultural Fit")}
          {renderStars("problemSolving", "Problem Solving")}
          {renderStars("roleKnowledge", "Role Knowledge")}
        </div>

        {(!isViewMode || ratings.additionalComments) && (
          <div className="feedback-comments-section">
            <label className="feedback-comments-label">Additional Comments (optional)</label>
            {isViewMode && ratings.additionalComments ? (
              <div className="feedback-comments-text">{ratings.additionalComments}</div>
            ) : (
              <textarea
                className="feedback-comments-textarea"
                placeholder="Share specific strengths or areas for improvement..."
                value={ratings.additionalComments}
                onChange={(e) => setRatings({ ...ratings, additionalComments: e.target.value })}
                rows={4}
                disabled={isViewMode}
                readOnly={isViewMode}
              />
            )}
          </div>
        )}

        <div className="feedback-overall-score">
          <span className="feedback-overall-label">Overall Score:</span>
          <span className="feedback-overall-value">{calculateOverallScore()} / 5.0</span>
        </div>

        <div className="feedback-modal-actions">
          <button className="btn-cancel" onClick={onCancel} disabled={isSubmitting}>
            {isViewMode ? "Close" : "Cancel"}
          </button>
          {!isViewMode && (
            <button className="btn-preview" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedbackModal;
